/** Vocabolari della cronologia di una pratica: azioni, categorie, campi.
 *
 * Stessa scelta — e stesso motivo — di `discardFamilies.ts`: la fonte di verità è
 * il backend, ma qui le chiavi arrivano dentro le righe di `audit_log` senza
 * etichetta, quindi servono da questo lato. Duplicazione consapevole e
 * circoscritta: una chiave non mappata viene mostrata **grezza** anziché come
 * trattino, così un'azione nuova resta leggibile anche prima di essere aggiunta.
 *
 * Le azioni non sono immaginate: sono quelle realmente scritte da `write_audit`
 * (`src/api/routes/audit.py:45`), verificate sui valori presenti in produzione il
 * 2026-08-04 — `approve` 285, `reject` 185, `correction` 26, `reopen` 4.
 */

export interface ActionMeta {
  label: string;
  /** Token Indaco, non un colore: il pannello non deve conoscere la tavolozza. */
  color: string;
  icon: string;
}

export const ACTION_META: Record<string, ActionMeta> = {
  approve: { label: "Approvata", color: "var(--indaco-success)", icon: "check_circle" },
  reject: { label: "Scartata", color: "var(--indaco-danger)", icon: "cancel" },
  discard: { label: "Scartata", color: "var(--indaco-gray-500)", icon: "delete_sweep" },
  correction: { label: "Corretta", color: "var(--indaco-info)", icon: "edit" },
  ddt_correction: { label: "Corretta", color: "var(--indaco-info)", icon: "edit" },
  bsc_correction: { label: "Corretta", color: "var(--indaco-info)", icon: "edit" },
  ddt_rematch_articoli: { label: "Riabbinati gli articoli", color: "var(--indaco-info)", icon: "sync_alt" },
  revert: { label: "Correzioni annullate", color: "var(--indaco-warning)", icon: "undo" },
  reopen: { label: "Riaperta", color: "var(--indaco-warning)", icon: "lock_open" },
  reprocess: { label: "Riprocessata", color: "var(--indaco-brand-primary)", icon: "restart_alt" },
  document_upload: { label: "Documento caricato", color: "var(--indaco-info)", icon: "upload_file" },
  document_delete: { label: "Documento eliminato", color: "var(--indaco-warning)", icon: "delete" },
};

export function actionMeta(action: string): ActionMeta {
  return ACTION_META[action] ?? { label: action, color: "var(--indaco-gray-500)", icon: "bolt" };
}

/** Categorie che l'operatore seleziona quando scarta.
 *
 * I quattro valori presenti in produzione (118 `non_pertinente`, 78 `duplicato`,
 * 17 `info_insufficienti`, 15 `test`). Il vocabolario **non ha** un'opzione per
 * "il sistema ha sbagliato": è la lacuna per cui i fallimenti nostri finiscono
 * sotto `duplicato` e `non_pertinente`, e per cui la famiglia ricavata dalla nota
 * è più affidabile della categoria — vedi `discardFamilies.ts`.
 */
export const REJECT_CATEGORY_LABELS: Record<string, string> = {
  non_pertinente: "Non pertinente",
  duplicato: "Duplicato",
  info_insufficienti: "Informazioni insufficienti",
  test: "Email di test",
  compliance: "Compliance",
  auto_reply: "Risposta automatica",
  commercial: "Commerciale",
  allarme_rientrato: "Allarme rientrato",
  non_specificata: "Non specificata",
};

export function rejectCategoryLabel(key: string): string {
  return REJECT_CATEGORY_LABELS[key] ?? key.replace(/_/g, " ");
}

/** Perché l'operatore ha corretto (`processing_corrections.correction_category`). */
export const CORRECTION_CATEGORY_LABELS: Record<string, string> = {
  estrazione_errata: "Estrazione errata",
  match_errato: "Abbinamento errato",
  match_mancante: "Abbinamento mancante",
  match_ambiguo: "Abbinamento ambiguo",
  dato_mancante: "Dato mancante",
  dato_obsoleto: "Dato obsoleto",
  dati_incompleti: "Dati incompleti",
  anagrafica_errata: "Anagrafica errata",
  ambiguita: "Ambiguità",
  manual_correction: "Correzione manuale",
  non_specificata: "Non specificata",
};

export function correctionCategoryLabel(key: string): string {
  return CORRECTION_CATEGORY_LABELS[key] ?? key.replace(/_/g, " ");
}

/** Nome leggibile del campo corretto.
 *
 * Su RDI `field_corrected` è la chiave dell'entità (in produzione solo `cliente`,
 * `edificio`, `impianto`: le sole che passano da `/correct`). Sul DDT è invece un
 * percorso dentro il JSONB con gli indici già normalizzati
 * (`righe.destinazioni.identificativo`), quindi il fallback compone i segmenti
 * invece di restituire la chiave intera, che sarebbe illeggibile.
 */
export const CORRECTED_FIELD_LABELS: Record<string, string> = {
  cliente: "Cliente",
  edificio: "Edificio",
  impianto: "Impianto",
  chiamante: "Chiamante",
  amministratore: "Amministratore",
  motivo: "Motivo",
  priorita: "Priorità",
};

export function correctedFieldLabel(key: string): string {
  const noto = CORRECTED_FIELD_LABELS[key];
  if (noto) return noto;
  if (key === "righe.__deleted__") return "Riga eliminata";
  const pezzi = key.split(".").filter((p) => p !== "righe");
  if (!pezzi.length) return key;
  const testo = pezzi.map((p) => p.replace(/_/g, " ")).join(" · ");
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}
